import { useEffect, useState } from 'react';

const SIZE_MAP = {
    sm: { wrap: 'w-14 h-16', trip: 8, score: 46, of: 7, tier: 8 },
    md: { wrap: 'w-24 h-28', trip: 10, score: 58, of: 9, tier: 10 },
    lg: { wrap: 'w-48 h-56', trip: 11, score: 68, of: 10, tier: 12 }
};

export default function ScoreShield({ score, tier, tierColor, size = 'lg' }) {
    const [displayScore, setDisplayScore] = useState(0);
    const dims = SIZE_MAP[size] || SIZE_MAP.lg;

  // Animate the count up
  useEffect(() => {
    if (typeof score !== 'number') return;
    const duration = 1400;
    const start = performance.now();
    const initialScore = displayScore;

    function step(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(initialScore + (score - initialScore) * eased);
      setDisplayScore(current);
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    // eslint-disable-next-line
  }, [score]);

  // Gradient stops based on score (option C from design discussion)
  // The gradient transitions through green->yellow->orange->red as score declines
  const gradientStops = getGradientStops(score);

  return (
        <div className={`relative ${dims.wrap}`}>
      <svg
        viewBox="0 0 200 240"
        className="w-full h-full drop-shadow-xl"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="shield-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={gradientStops.top} />
            <stop offset="100%" stopColor={gradientStops.bottom} />
          </linearGradient>
          <linearGradient id="shield-inner" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0.2" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Shield shape — based on Understudy logo silhouette */}
        <path
          d="M 100 5
             L 185 35
             L 185 130
             Q 185 175 100 230
             Q 15 175 15 130
             L 15 35
             Z"
          fill="url(#shield-gradient)"
          stroke="#1B2B4B"
          strokeWidth="2"
        />

        {/* Inner highlight for depth */}
        <path
          d="M 100 12
             L 178 38
             L 178 128
             Q 178 170 100 222
             Q 22 170 22 128
             L 22 38
             Z"
          fill="url(#shield-inner)"
        />

        {/* Top label */}
        <text
          x="100"
          y="55"
          textAnchor="middle"
          fontFamily="Open Sans, sans-serif"
          fontWeight="700"
          fill="white"
          fontSize={dims.trip}
          opacity="0.92"
        >
          TRIP
        </text>

        {/* Score number */}
        <text
          x="100"
          y="140"
          textAnchor="middle"
          fontFamily="Merriweather, Georgia, serif"
          fontSize={dims.score}
          fontWeight="900"
          fill="white"
          className="select-none"
        >
          {displayScore}
        </text>

        {/* Out of 100 mini */}
        <text
          x="100"
          y="162"
          textAnchor="middle"
          fontFamily="Open Sans, sans-serif"
          fontSize={dims.of}
          fill="white"
          opacity="0.7"
          letterSpacing="2"
        >
          OF 100
        </text>

        {/* Tier label */}
        <text
          x="100"
          y="195"
          textAnchor="middle"
          fontFamily="Open Sans, sans-serif"
          fontSize={dims.tier}
          fontWeight="700"
          fill="white"
          letterSpacing="2"
          opacity="0.95"
        >
          {(tier || '').toUpperCase()}
        </text>
      </svg>
    </div>
  );
}

/**
 * Get gradient stops based on score. Creates a 2-stop gradient where
 * top is lighter and bottom is the tier color.
 */
function getGradientStops(score) {
  // Higher scores = green, lower scores = red
  if (score >= 85) return { top: '#34D399', bottom: '#15803D' };  // green
  if (score >= 70) return { top: '#A3E635', bottom: '#65A30D' };  // lime
  if (score >= 55) return { top: '#FCD34D', bottom: '#D97706' };  // orange
  if (score >= 35) return { top: '#FB923C', bottom: '#B91C1C' };  // red-orange
  return { top: '#EF4444', bottom: '#7F1D1D' };                   // deep red
}
